import {
  DialogContent,
  TableHead,
  InputAdornment,
  Table,
  TableContainer,
  TableBody,
  Box,
} from '@mui/material';
import { CloseRounded, SearchRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import {
  downloadDialog,
  downloadTable,
} from '~/components/Global/Dialog/Dialog.style';
import { TDownloadableResource } from '~/types/common.type';
import {
  openMode,
  useDownload,
  isWebBatchDownloadOverSizeLimit,
} from '~/utils/hook/useDownload';
import { AlertDialog } from '~/components/Global/Dialog/AlertDialog';
import { THandleSelectedList, DownloadDialogItem } from './DownloadDialogItem';
import { SelectType } from '~/types/enum';
import {
  canSelectForDownload,
  pruneUnselectable,
} from '~/utils/helper/downloadFile';
import { downloadAllStartEvent } from '~/tracking';

type TProp = {
  handleClose: () => void;
  open: boolean;
  labels: string[];
  currentList: TDownloadableResource[];
};

export const DownloadDialog = ({
  handleClose,
  open,
  labels,
  currentList,
}: TProp) => {
  const [searchKey, setSearchKey] = useState('');
  const [selectedList, setSelectedList] = useState<TDownloadableResource[]>([]);
  const { multipleFileDownload } = useDownload();

  const overview = useMemo(() => {
    let downloadableCount = 0;
    const list: (TDownloadableResource & { hasCheckbox: boolean })[] = [];

    currentList.forEach(({ progress, resource }) => {
      const { fileName } = resource.file;
      const escapedSearchKey = searchKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const searchRegex = new RegExp(`${escapedSearchKey}`, 'g');
      if (fileName.match(searchRegex)) {
        const hasCheckbox = canSelectForDownload({ progress, resource });
        if (hasCheckbox) {
          downloadableCount++;
        }
        list.push({
          progress,
          resource,
          hasCheckbox,
        });
      }
    });

    return { list, downloadableCount };
  }, [currentList, labels, searchKey]);

  const handleSelectedList = ({ type, data }: THandleSelectedList) => {
    switch (type) {
      case SelectType.addAll: {
        setSelectedList(overview.list.filter(({ hasCheckbox }) => hasCheckbox));
        break;
      }
      case SelectType.clearAll: {
        setSelectedList([]);
        break;
      }
      case SelectType.add: {
        data && setSelectedList(prev => [...prev, data]);
        break;
      }
      case SelectType.clear: {
        data &&
          setSelectedList(prev =>
            prev.filter(
              item => item.resource.file.url !== data.resource.file.url,
            ),
          );
        break;
      }
      default: {
        break;
      }
    }
  };

  const overallStatus = useMemo(() => {
    const hasSelected = selectedList.length > 0;
    return {
      isSelectable: overview.downloadableCount > 0,
      hasSelected,
      hasAllSelected:
        selectedList.length === overview.downloadableCount && hasSelected,
      indeterminate:
        hasSelected && selectedList.length < overview.downloadableCount,
    };
  }, [selectedList, overview.downloadableCount, searchKey]);

  const [showSizeLimitAlert, setShowSizeLimitAlert] = useState(false);

  const batchDownload = () => {
    // 網頁版 zip 打包超過 4GiB 會產生損毀檔，超限時擋下並提示逐檔下載
    if (isWebBatchDownloadOverSizeLimit(selectedList)) {
      setShowSizeLimitAlert(true);
      return;
    }
    const firstResource = selectedList[0]?.resource;
    if (firstResource) {
      downloadAllStartEvent({
        edu: firstResource.eduCodes[0],
        subject: firstResource.subjects[0],
        category: firstResource.resources1[0],
        resources_label: firstResource.resources2[0],
        resources_sub_label: firstResource.resources3[0],
      });
    }
    multipleFileDownload({
      list: selectedList,
      mode: openMode.link,
    });
    setSelectedList([]);
  };

  useEffect(() => {
    setSelectedList([]);
  }, [open]);

  // 依完整的 currentList 判斷，不用搜尋後的清單：被搜尋暫時藏起來的項目不該因此取消勾選
  useEffect(() => {
    setSelectedList(prev => pruneUnselectable(prev, currentList));
  }, [currentList]);

  return (
    <downloadDialog.dialog open={open} onClose={handleClose}>
      <downloadDialog.title>
        <downloadDialog.titleContent>
          <downloadDialog.titleText>單一檔案下載</downloadDialog.titleText>
          <downloadDialog.textfield
            value={searchKey}
            onChange={event => {
              setSearchKey(event.target.value);
            }}
            placeholder="請輸入搜尋名稱"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
            }}
          />
        </downloadDialog.titleContent>
        <CloseRounded
          sx={{ fontSize: '26px', cursor: 'pointer' }}
          onClick={handleClose}
        />
      </downloadDialog.title>
      <DialogContent sx={{ overflow: 'hidden' }}>
        <TableContainer component="div" sx={{ height: '100%' }}>
          <Table stickyHeader sx={{ overflow: 'auto' }}>
            <TableHead>
              <downloadTable.headRow>
                {overallStatus.isSelectable && (
                  <downloadTable.cell width="0px" isHeader>
                    <downloadTable.checkbox
                      checked={overallStatus.hasSelected}
                      indeterminate={overallStatus.indeterminate}
                      onChange={(_, checked: boolean) => {
                        checked
                          ? handleSelectedList({ type: SelectType.addAll })
                          : handleSelectedList({
                              type: SelectType.clearAll,
                            });
                      }}
                    />
                  </downloadTable.cell>
                )}
                <downloadTable.cell width="100%" isHeader>
                  {overallStatus.hasSelected
                    ? `已選取 ${selectedList.length} 項`
                    : '檔案名稱'}
                </downloadTable.cell>
                <downloadTable.cell width="120px" isHeader>
                  {!overallStatus.hasSelected && '檔案大小'}
                </downloadTable.cell>
                <downloadTable.cell width="232px" isHeader>
                  {overallStatus.hasSelected && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <downloadTable.batchDownloadButton
                        variant="contained"
                        onClick={batchDownload}
                      >
                        批次下載
                      </downloadTable.batchDownloadButton>
                    </Box>
                  )}
                </downloadTable.cell>
              </downloadTable.headRow>
            </TableHead>
            <TableBody>
              {overview.list.map(data => {
                const isChecked = !!selectedList.find(
                  item => item.resource.file.url === data.resource.file.url,
                );
                return (
                  <DownloadDialogItem
                    data={data}
                    key={data.resource.file.url}
                    isChecked={isChecked && data.hasCheckbox}
                    hasCheckBox={data.hasCheckbox}
                    handleSelectedList={handleSelectedList}
                    isOverallSelectable={overallStatus.isSelectable}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <AlertDialog
        open={showSizeLimitAlert}
        title="批次下載提醒"
        onClose={() => setShowSizeLimitAlert(false)}
      >
        所選檔案總計超過 3.8GB，超出批次打包上限，
        <br />
        可能導致下載檔案損毀。
        <br />
        請改用單一檔案方式逐一下載。
      </AlertDialog>
    </downloadDialog.dialog>
  );
};
